import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Image,
  Input,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  Flex,
} from "@chakra-ui/react";
import axios from "axios";

const ImageUploadComponent = ({ apiUrl, title, desc }) => {
  const [selectedFile, setSelectedFile] = useState(null);

  // เก็บทั้ง URL และ Blob ของผลลัพธ์เพื่อใช้ดาวน์โหลดได้ทันที
  const [outputImage, setOutputImage] = useState(null); // object URL
  const [outputBlob, setOutputBlob] = useState(null);   // Blob จริง

  const [originalImageUrl, setOriginalImageUrl] = useState(null); // object URL ของรูปต้นฉบับ

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reference for the file input element
  const fileInputRef = useRef(null);

  // เคลียร์ URL เก่าเมื่อค่ามันเปลี่ยน ป้องกัน memory leak
  useEffect(() => {
    return () => {
      if (originalImageUrl) URL.revokeObjectURL(originalImageUrl);
      if (outputImage) URL.revokeObjectURL(outputImage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // cleanup เมื่อ component ถูก unmount

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    // ล้าง URL เก่าถ้ามี
    if (originalImageUrl) URL.revokeObjectURL(originalImageUrl);
    if (outputImage) URL.revokeObjectURL(outputImage);

    setSelectedFile(file);
    setOutputImage(null);
    setOutputBlob(null);
    setError(null);

    // สร้าง object URL ของรูปต้นฉบับไว้แสดงผล
    if (file) {
      const url = URL.createObjectURL(file);
      setOriginalImageUrl(url);
    } else {
      setOriginalImageUrl(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select an image to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("image", selectedFile);

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(apiUrl, formData, {
        responseType: "blob",
        headers: {
          "X-API-KEY": process.env.REACT_APP_MOUTH_API_KEY,
        },
      });

      // ล้าง URL เดิมของผลลัพธ์ (ถ้ามี)
      if (outputImage) URL.revokeObjectURL(outputImage);

      const blob = response.data;
      const url = URL.createObjectURL(blob);

      setOutputBlob(blob);
      setOutputImage(url);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error ||
          "An error occurred while uploading the image."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!outputImage && !outputBlob) return;

    try {
      // ใช้ Blob จาก state ถ้ามี ไม่ต้อง fetch ซ้ำ
      let blob = outputBlob;

      if (!blob && outputImage) {
        // เผื่อกรณีที่ไม่มี blob ติดไว้ (จะไม่เกิดจากโค้ดนี้ แต่เผื่อไว้)
        const res = await fetch(outputImage);
        blob = await res.blob();
      }

      const a = document.createElement("a");
      const base =
        selectedFile?.name?.replace(/\.[^/.]+$/, "") || "result";
      a.download = `${base}-detected.png`;
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      console.error(e);
      setError("Failed to download the image.");
    }
  };

  const handleClear = () => {
    // เคลียร์ URL ทั้งหมด
    if (originalImageUrl) URL.revokeObjectURL(originalImageUrl);
    if (outputImage) URL.revokeObjectURL(outputImage);

    setSelectedFile(null);
    setOriginalImageUrl(null);
    setOutputImage(null);
    setOutputBlob(null);
    setError(null);
    setIsLoading(false);

    // Clear the file input field
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  return (
    <VStack spacing={6} align="center" w="full" py={8} px={4}>
      <Box textAlign="center">
        <Text fontSize="2xl" fontWeight="bold" mb={2} color="teal.600">
          {title}
        </Text>
        <Text fontSize="md" color="gray.500">
          {desc ? desc : "Upload an image and let the system detect."}
        </Text>
      </Box>

      <Input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={isLoading}
        variant="filled"
        focusBorderColor="teal.400"
      />

      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <HStack spacing={4}>
        <Button
          colorScheme="teal"
          onClick={handleUpload}
          isLoading={isLoading}
          loadingText="Processing..."
          isDisabled={!selectedFile || isLoading}
          size="lg"
          shadow="md"
        >
          Upload and Detect
        </Button>

        <Button
          colorScheme="orange"
          onClick={handleClear}
          isDisabled={isLoading}
          size="lg"
          shadow="md"
        >
          Clear
        </Button>

        <Button
          colorScheme="blue"
          onClick={handleDownload}
          isDisabled={!outputImage || isLoading}
          size="lg"
          shadow="md"
        >
          Download Result
        </Button>
      </HStack>

      {isLoading && <Spinner size="xl" thickness="4px" color="teal.500" />}

      {originalImageUrl && outputImage && (
        <Flex mt={6} justify="center" align="center" gap={6} wrap="wrap">
          <Box textAlign="center">
            <Text fontSize="lg" fontWeight="bold" color="teal.600" mb={4}>
              Original Image
            </Text>
            <Image
              src={originalImageUrl}
              alt="Original Image"
              boxSize="400px"
              objectFit="contain"
              borderRadius="lg"
              border="2px solid"
              borderColor="teal.300"
              shadow="lg"
            />
          </Box>
          <Box textAlign="center">
            <Text fontSize="lg" fontWeight="bold" color="teal.600" mb={4}>
              Detection Result
            </Text>
            <Image
              src={outputImage}
              alt="Output Image"
              boxSize="400px"
              objectFit="contain"
              borderRadius="lg"
              border="2px solid"
              borderColor="teal.300"
              shadow="lg"
            />
          </Box>
        </Flex>
      )}
    </VStack>
  );
};

export default ImageUploadComponent;


/*
import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Image,
  Input,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  Flex,
} from "@chakra-ui/react";
import axios from "axios";

const ImageUploadComponent = ({ apiUrl, title, desc }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [outputImage, setOutputImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reference for the file input element
  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setOutputImage(null); // Clear previous output image
    setError(null); // Clear previous errors
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select an image to upload.");
      return;
    }
  
    const formData = new FormData();
    formData.append("image", selectedFile);
  
    setIsLoading(true);
    setError(null);
  
    try {
      console.log("apiUrl:", apiUrl);
      const response = await axios.post(apiUrl, formData, {
        responseType: "blob",
        headers: {
          "X-API-KEY": process.env.REACT_APP_MOUTH_API_KEY
        }
      });
  
      const outputImageUrl = URL.createObjectURL(response.data);
      setOutputImage(outputImageUrl);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.error || "An error occurred while uploading the image."
      );
    } finally {
      setIsLoading(false);
    }
  };  

  const handleClear = () => {
    setSelectedFile(null);
    setOutputImage(null);
    setError(null);
    setIsLoading(false);

    // Clear the file input field
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const originalImageUrl = selectedFile ? URL.createObjectURL(selectedFile) : null;

  return (
    <VStack spacing={6} align="center" w="full" py={8} px={4}>
      <Box textAlign="center">
        <Text fontSize="2xl" fontWeight="bold" mb={2} color="teal.600">
          {title}
        </Text>
        <Text fontSize="md" color="gray.500">
          {desc ? desc : "Upload an image and let the system detect."}
        </Text>
      </Box>

      <Input
        ref={fileInputRef} // Attach reference to the input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={isLoading}
        variant="filled"
        focusBorderColor="teal.400"
      />

      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <HStack spacing={4}>
        <Button
          colorScheme="teal"
          onClick={handleUpload}
          isLoading={isLoading}
          loadingText="Processing..."
          isDisabled={!selectedFile || isLoading}
          size="lg"
          shadow="md"
        >
          Upload and Detect
        </Button>
        <Button
          colorScheme="orange"
          onClick={handleClear}
          isDisabled={isLoading}
          size="lg"
          shadow="md"
        >
          Clear
        </Button>
      </HStack>

      {isLoading && <Spinner size="xl" thickness="4px" color="teal.500" />}

      {originalImageUrl && outputImage && (
        <Flex mt={6} justify="center" align="center" gap={6}>
          <Box textAlign="center">
            <Text fontSize="lg" fontWeight="bold" color="teal.600" mb={4}>
              Original Image
            </Text>
            <Image
              src={originalImageUrl}
              alt="Original Image"
              boxSize="400px"
              objectFit="contain"
              borderRadius="lg"
              border="2px solid"
              borderColor="teal.300"
              shadow="lg"
            />
          </Box>
          <Box textAlign="center">
            <Text fontSize="lg" fontWeight="bold" color="teal.600" mb={4}>
              Detection Result
            </Text>
            <Image
              src={outputImage}
              alt="Output Image"
              boxSize="400px"
              objectFit="contain"
              borderRadius="lg"
              border="2px solid"
              borderColor="teal.300"
              shadow="lg"
            />
          </Box>
        </Flex>
      )}
    </VStack>
  );
};

export default ImageUploadComponent;
*/